import { createColumnHelper } from "@tanstack/react-table";
import { moneyDisplay } from "@icraftx/shared/v1";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import { toggleFilter } from "@/modules/core/list-cell-actions";
import { DeskDocListRowMenu } from "@/modules/shell/DeskDocListRowMenu";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import { stashListReturn } from "@/modules/shell/list-return-state";
import { relativeTimeLabel } from "@/modules/shell/relative-time";
import type { PosTicketListRow } from "@/shared/api/pos";

const columnHelper = createColumnHelper<DeskTableFeatures, PosTicketListRow>();

export type PosTicketListCellActions = {
  listPath: string;
  terminalId: string;
  setTerminalId: (next: string) => void;
};

function formatWhen(iso: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export function posTicketListColumns(
  companySlug: string,
  actions: PosTicketListCellActions,
) {
  return columnHelper.columns([
    columnHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 120,
      cell: (info) => {
        const row = info.row.original;
        const docTo = `/c/${companySlug}/pos/tickets/${row.id}`;
        return (
          <DocNumberLink
            to={docTo}
            onClick={() => stashListReturn(actions.listPath, row.id)}
          >
            {info.getValue()}
          </DocNumberLink>
        );
      },
    }),
    columnHelper.accessor("terminalName", {
      id: "terminalName",
      header: "Terminal",
      size: 140,
      cell: (info) => {
        const tid = info.row.original.terminalId;
        if (!tid) return info.getValue() ?? "—";
        return (
          <button
            type="button"
            className="cursor-pointer text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
            title={`Filter: ${info.getValue()}`}
            onClick={() =>
              actions.setTerminalId(
                toggleFilter(actions.terminalId, tid, ""),
              )
            }
          >
            {info.getValue()}
          </button>
        );
      },
    }),
    columnHelper.accessor("lifecycle", {
      id: "lifecycle",
      header: "Status",
      size: 88,
      cell: (info) => (
        <LifecycleStatus
          value={info.getValue()}
          to={`/c/${companySlug}/pos/tickets/${info.row.original.id}`}
        />
      ),
    }),
    columnHelper.accessor("grossTotal", {
      id: "grossTotal",
      header: "Gross",
      size: 110,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row.postedAt ?? row.createdAt, {
      id: "createdAt",
      header: "When",
      size: 170,
      cell: (info) => (
        <span className="text-[var(--color-muted)]">
          {formatWhen(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor("syncStatus", {
      id: "syncStatus",
      header: "Sync",
      size: 100,
      enableSorting: false,
      cell: (info) => {
        const v = info.getValue();
        return v === "synced" ? (
          <span className="text-[var(--color-muted)]">—</span>
        ) : (
          v
        );
      },
    }),
    columnHelper.accessor("updatedAt", {
      id: "updatedAt",
      header: "Updated",
      size: 88,
      enableSorting: false,
      cell: (info) => {
        const raw = info.getValue();
        return raw ? (
          <span className="text-xs text-[var(--color-muted)] tabular-nums">
            {relativeTimeLabel(raw)}
          </span>
        ) : (
          "—"
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 48,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const docPath = `/c/${companySlug}/pos/tickets/${row.original.id}`;
        return <DeskDocListRowMenu docPath={docPath} tabLinks={[]} />;
      },
    }),
  ]);
}
